import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Code2, GraduationCap, Sparkles, Users } from "lucide-react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import Portfolio from "../components/Proof";
import TownhallRecognition from "../components/TownhallRecognition";
import { FOUNDER } from "../data/content";

const PILLARS = [
    { icon: Code2, title: "Technology that ships", text: "Web, mobile, internal tools and QA - production-grade engineering at sensible economics." },
    { icon: GraduationCap, title: "Talent we grow", text: "Structured tracks turn motivated rural and urban learners into client-ready engineers." },
    { icon: Sparkles, title: "AI without the theatre", text: "Audits, workshops and embedded advisors that turn AI into measurable workflows." },
    { icon: Users, title: "Community-led growth", text: "Local pipelines with institutions so opportunity moves outwards, not just upwards." },
];

const About = () => (
    <PageLayout title="About IndoBlitz · Built rural. Delivered globally." testId="about-page">
        <PageHero
            eyebrow="About Us"
            title="A technology & talent firm,"
            accent="built rural."
            intro="IndoBlitz is a cross-border technology and talent company engineered in Loni, India and delivering to Sydney, Dubai, London, Singapore and beyond. We believe geography should never decide the quality of a person's future - or the quality of the software a business can afford."
            testId="about-hero"
        />

        {/* What we do */}
        <section className="relative py-10 lg:py-14" data-testid="about-pillars">
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="grid sm:grid-cols-2 gap-4">
                    {PILLARS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className="glass rounded-2xl p-7"
                                data-testid={`about-pillar-${i}`}
                            >
                                <div className="w-11 h-11 rounded-xl bg-gold/10 border border-gold/15 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-gold" />
                                </div>
                                <h3 className="mt-5 font-display text-2xl text-ivory leading-tight">{p.title}</h3>
                                <p className="mt-3 text-ivory/65 leading-relaxed text-sm">{p.text}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>

        {/* Founder teaser - company-first, founder within */}
        <section className="relative py-10 lg:py-14" data-testid="about-founder-teaser">
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <Link
                    to="/founder"
                    className="block glass glass-hover rounded-2xl overflow-hidden group"
                    data-testid="about-founder-link"
                >
                    <div className="grid md:grid-cols-12">
                        <div className="md:col-span-4 relative aspect-[4/5] md:aspect-auto md:min-h-[280px] overflow-hidden bg-gradient-to-br from-[#1A1A22] to-obsidian">
                            <img
                                src={FOUNDER.image}
                                alt={`${FOUNDER.name} - ${FOUNDER.title}`}
                                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                                loading="lazy"
                            />
                        </div>
                        <div className="md:col-span-8 p-7 lg:p-10 flex flex-col justify-center">
                            <div className="pill"><span className="accent-dot" />Meet the founder</div>
                            <h3 className="mt-4 font-display text-3xl lg:text-4xl text-ivory leading-tight">{FOUNDER.name}</h3>
                            <p className="mt-2 font-display italic text-lg text-gold-shimmer">{FOUNDER.tagline}</p>
                            <p className="mt-4 text-ivory/65 leading-relaxed text-sm max-w-2xl">
                                30+ years across India, Japan and Australia · founder of IndoBlitz, Pragya Shakti Foundation
                                and Bobby Philips Production · producer and lead actor of the 202-award short film RED.
                            </p>
                            <span className="mt-6 inline-flex items-center gap-2 text-gold text-sm font-medium">
                                Read the full story
                                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                            </span>
                        </div>
                    </div>
                </Link>
            </div>
        </section>

        {/* Team recognition - townhall awardees */}
        <TownhallRecognition />

        {/* Work + testimonials */}
        <Portfolio />
    </PageLayout>
);

export default About;
